import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, Paper, Typography, Button } from '@mui/material';
import vibetideTheme from './theme/vibetideTheme';

// Lazy load della Home e delle pagine Dashboard
const HomePage = React.lazy(() => import('./pages/HomePage'));
const LayoutReal = React.lazy(() => import('./components/Layout/Layout'));
const DashboardReal = React.lazy(() => import('./pages/DashboardPage'));
const EventsPage = React.lazy(() => import('./pages/EventsPage'));
const EventSettingsPage = React.lazy(() => import('./pages/EventSettingsPage'));
const EventDisplayPage = React.lazy(() => import('./pages/EventDisplayPage'));
// MessagesPage rimosso - ora tutto è nel tab Messaggi di EventSettingsPage
const SponsorsPage = React.lazy(() => import('./pages/SponsorsPage'));
const PartecipaPage = React.lazy(() => import('./pages/PartecipaPage'));
const PartecipantiPage = React.lazy(() => import('./pages/PartecipantiPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = React.lazy(() => import('./pages/TermsOfServicePage'));
const CookiePolicyPage = React.lazy(() => import('./pages/CookiePolicyPage'));

const SafePage = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Arial, sans-serif',
    background: '#f5f5f5',
    color: '#222',
    padding: '24px',
    boxSizing: 'border-box'
  }}>
    <div style={{ textAlign: 'center', maxWidth: 720 }}>
      <img src="/vibetide-logo.png" alt="VibeTide" style={{ height: 54, marginBottom: 16 }} />
      <div style={{ fontSize: 22, fontWeight: 600, marginBottom: 8 }}>VibeTide è online</div>
      <div style={{ marginBottom: 16, opacity: 0.8 }}>
        Modalità di sicurezza attiva temporaneamente. La dashboard completa verrà ripristinata a breve.
      </div>
      <div style={{ fontSize: 14, opacity: 0.65 }}>
        Se vedi ancora questa schermata dopo l'aggiornamento, prova un hard refresh (Cmd/Ctrl + Shift + R).
      </div>
    </div>
  </div>
);

const LoginSafe = () => (
  <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
    <Paper elevation={3} sx={{ p: 4, maxWidth: 420, width: '100%', textAlign: 'center' }}>
      <img src="/vibetide-logo.png" alt="VibeTide" style={{ height: 40, marginBottom: 12 }} />
      <Typography variant="h6" sx={{ mb: 1 }}>Accesso</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Login temporaneamente non disponibile in SAFE MODE.
      </Typography>
      <Button variant="contained" disabled>Accedi</Button>
    </Paper>
  </Box>
);

const DashboardSafe = () => (
  <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
    <Paper elevation={2} sx={{ p: 4, maxWidth: 720, width: '100%', textAlign: 'center' }}>
      <img src="/vibetide-logo.png" alt="VibeTide" style={{ height: 40, marginBottom: 12 }} />
      <Typography variant="h6" sx={{ mb: 1 }}>Dashboard (SAFE MODE)</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        La dashboard completa sarà ripristinata progressivamente. Le funzioni principali torneranno a breve.
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Token e organizzazione sono stati salvati dopo l'accesso OAuth.
      </Typography>
    </Paper>
  </Box>
);

function OAuthCallbackSafe() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const token = params.get('token');
      const refreshToken = params.get('refreshToken');
      const userStr = params.get('user');

      // Salva in localStorage
      if (token) localStorage.setItem('authToken', token);
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);

      let orgSlug = null;
      let userData = null;

      if (userStr) {
        try {
          userData = JSON.parse(decodeURIComponent(userStr));
          if (userData?.organization?.slug) {
            orgSlug = userData.organization.slug;
            localStorage.setItem('organizationSlug', orgSlug);
          }
          localStorage.setItem('userData', JSON.stringify(userData));

          // IMPORTANTE: Aggiorna anche lo store Zustand persistito
          const zustandState = {
            state: {
              user: userData,
              organization: userData?.organization || null,
              token: token,
              refreshToken: refreshToken,
              isAuthenticated: true
            },
            version: 0
          };
          localStorage.setItem('vibetide-auth', JSON.stringify(zustandState));
        } catch (_) { }
      }

      if (!orgSlug && token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload?.email) {
            orgSlug = payload.email.split('@')[0]
              .toLowerCase()
              .replace(/[^a-z0-9]/g, '-')
              .replace(/-+/g, '-')
              .replace(/^-|-$/g, '')
              .substring(0, 30);
            if (orgSlug) localStorage.setItem('organizationSlug', orgSlug);
          }
        } catch (_) { }
      }

      // Forza reload per far leggere i nuovi dati dallo store
      const dest = orgSlug ? `/${orgSlug}/dashboard` : '/dashboard';
      window.location.href = dest;
    } catch (e) {
      console.error('OAuth callback error:', e);
      navigate('/safe', { replace: true });
    }
  }, [location, navigate]);

  return <SafePage />;
}

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) { console.error('ErrorBoundary:', error, info); }
  render() { return this.state.hasError ? <DashboardSafe /> : this.props.children; }
}

function App() {
  return (
    <ThemeProvider theme={vibetideTheme}>
      <CssBaseline />
      <Router>
        <Routes>
          {/* Public */}
          <Route
            path="/"
            element={
              <ErrorBoundary>
                <Suspense fallback={<SafePage />}>
                  <HomePage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route path="/login" element={<LoginSafe />} />
          <Route path="/safe" element={<SafePage />} />
          <Route path="/oauth-callback" element={<OAuthCallbackSafe />} />
          <Route path="/privacy-policy" element={
            <Suspense fallback={<SafePage />}>
              <PrivacyPolicyPage />
            </Suspense>
          } />
          <Route path="/terms-of-service" element={
            <Suspense fallback={<SafePage />}>
              <TermsOfServicePage />
            </Suspense>
          } />
          <Route path="/cookie-policy" element={
            <Suspense fallback={<SafePage />}>
              <CookiePolicyPage />
            </Suspense>
          } />

          {/* Pagina pubblica registrazione partecipanti */}
          <Route path="/partecipa/:eventoId" element={
            <Suspense fallback={<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>Caricamento...</Box>}>
              <PartecipaPage />
            </Suspense>
          } />

          {/* Dashboard base */}
          <Route
            path="/dashboard"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <DashboardReal />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />

          {/* Sezioni Dashboard */}
          <Route
            path="/events"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <EventsPage />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="/events/:eventId/settings"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <EventSettingsPage />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />
          {/* Route /messages rimossa - reindirizza a settings con tab Messaggi */}
          <Route
            path="/messages"
            element={<Navigate to="/dashboard" replace />}
          />
          <Route
            path="/sponsors"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <SponsorsPage />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="/partecipanti"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <PartecipantiPage />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />

          {/* Varianti multi-tenant con slug */}
          <Route
            path=":organizationSlug/dashboard"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <DashboardReal />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path=":organizationSlug/events"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <EventsPage />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path=":organizationSlug/events/:eventId/settings"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <EventSettingsPage />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />
          {/* Route :organizationSlug/messages rimossa - reindirizza a settings con tab Messaggi */}
          <Route
            path=":organizationSlug/messages"
            element={<Navigate to="../dashboard" replace />}
          />
          <Route
            path=":organizationSlug/sponsors"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <SponsorsPage />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path=":organizationSlug/partecipanti"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <PartecipantiPage />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />

          {/* Display Routes */}
          <Route
            path="/display/event-display/:eventId"
            element={
              <ErrorBoundary>
                <Suspense fallback={<SafePage />}>
                  <EventDisplayPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path=":organizationSlug/display/event-display/:eventId"
            element={
              <ErrorBoundary>
                <Suspense fallback={<SafePage />}>
                  <EventDisplayPage />
                </Suspense>
              </ErrorBoundary>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App; 